import { useState } from 'react';
import { useReload } from './useReload';
import { useAbortEffect } from './useEffects';
import { isFunction } from 'lodash';

export type ApiRequestParams = {
  loader: (params: { abortCtrl: AbortController }) => Promise<any>;
  defaultValue?: any;
  breakEffect?: () => boolean;
  parser?: (resp: any) => any;
  dependencies?: any[];
  onError?: (e: any) => void;
  cleanupFn?: () => void;
  debug?: boolean;
};

export const useApiRequest = <T>({
  // required
  defaultValue,
  loader,

  // optional
  parser,
  breakEffect = () => false,
  dependencies = [],
  onError,
  cleanupFn,
  debug = false,
}: ApiRequestParams): {
  state: T;
  setState: (data: T) => void;
  reloadCount: number;
  refresh: () => void;
  isLoading: boolean;
  error: any;
} => {
  const [state, setState] = useState(defaultValue);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reload, setReload] = useReload();

  useAbortEffect(
    (abortCtrl: AbortController) => {
      if (breakEffect()) {
        setIsLoading(false);
        return;
      }

      const query = async () => {
        setIsLoading(true);

        try {
          let resp = await loader({ abortCtrl });
          if (isFunction(parser)) resp = parser(resp);
          setError(null);
          setState(resp);
        } catch (e: any) {
          if (debug) console.error(e);
          setError(e);
          setState(defaultValue);
          isFunction(onError) && onError(e);
        } finally {
          setIsLoading(false);
        }
      };

      query();
      return cleanupFn;
    },
    [reload, ...dependencies]
  );

  return {
    state,
    setState,
    reloadCount: reload as number,
    refresh: setReload as () => void,
    isLoading,
    error,
  };
};
